import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';

import {
	PEOPLE_LEAVE_ENTITLEMENT_NAMES,
	PEOPLE_UNAVAILABILITY_KIND,
} from '../../../common/constants';
import {
	CREATE_MY_UNAVAILABILITY,
	MODIFY_MY_UNAVAILABILITY,
	REPEAT_MY_UNAVAILABILITY,
} from '../../../common/graphql/people/roster';
import { GET_EMPLOYEE_LEAVE_BALANCES } from '../../../common/graphql/people/employee';
import { getVisibleLeaveBalances } from '../../../common/utils/people/leaveBalance';
import {
	buildRosterRecurringDates,
	getRosterWeekday,
	getSelfServiceUnavailabilityCutoffDate,
	getTodayDeviceRosterDate,
	isSelfServiceUnavailabilityShortNotice,
	SELF_SERVICE_UNAVAILABILITY_NOTICE_MESSAGE,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import useMyRosterProfile from './useMyRosterProfile';
import useRefetchMyRosterEntries from './useRefetchMyRosterEntries';

const DEFAULT_START_MINUTES = 9 * 60;
const DEFAULT_END_MINUTES = 17 * 60;

/**
 * Request leave / add unavailability form state for LeaveRequestForm: the
 * fields (starting from `editingEntry` when editing), leave types and Xero
 * balances, the repeat dates, whether the entry may be submitted, and
 * submitEntry() (create, modify or repeat; goes back once saved).
 */
const useLeaveRequestForm = ({ kind, editingEntry }) => {
	const navigation = useNavigation();
	const { isAVC } = useUserContext();
	const {
		employmentTypeReady,
		canRequestLeave,
		canRequestUnavailability,
		allowedLeaveTypes,
	} = useMyRosterProfile();
	const refetchEntries = useRefetchMyRosterEntries();

	// Device-local day: the driver/picker is physically in their state.
	const today = useMemo(() => getTodayDeviceRosterDate(), []);
	const selfServiceCutoffDate = useMemo(() => getSelfServiceUnavailabilityCutoffDate(today), [today]);
	const isLeaveForm = kind === PEOPLE_UNAVAILABILITY_KIND.LEAVE;

	const [selectedLeaveType, setLeaveType] = useState(editingEntry?.leaveType ?? null);
	const [startDate, setStartDate] = useState(editingEntry?.startDate || '');
	const [endDate, setEndDate] = useState(editingEntry?.endDate || editingEntry?.startDate || '');
	const [allDay, setAllDay] = useState(editingEntry ? editingEntry.allDay !== false : true);
	const [startMinutes, setStartMinutes] = useState(editingEntry?.startMinutes ?? DEFAULT_START_MINUTES);
	const [endMinutes, setEndMinutes] = useState(editingEntry?.endMinutes ?? DEFAULT_END_MINUTES);
	const [notes, setNotes] = useState(editingEntry?.notes || '');
	const [repeatEnabled, setRepeatEnabled] = useState(false);
	const [repeatUntilDate, setRepeatUntilDate] = useState('');
	const [repeatIntervalWeeks, setRepeatIntervalWeeks] = useState(1);
	const [repeatWeekdays, setRepeatWeekdays] = useState([]);
	const [formError, setFormError] = useState('');
	// The allowed types arrive with the profile; until a type is picked, default to the first.
	const leaveType = selectedLeaveType ?? allowedLeaveTypes[0] ?? null;

	// Only the Leave form shows balances. The Leave page has just loaded them, so reuse the cache
	// rather than calling Xero again. A missing Xero link or Xero failure returns [].
	const { data: leaveBalanceData } = useQuery(GET_EMPLOYEE_LEAVE_BALANCES, {
		skip: !isAVC || !isLeaveForm || !canRequestLeave,
		fetchPolicy: 'cache-first',
	});
	const leaveBalances = useMemo(() => (
		getVisibleLeaveBalances(leaveBalanceData?.getEmployeeLeaveBalances)
	), [leaveBalanceData]);

	const leaveTypeOptions = useMemo(() => {
		const options = allowedLeaveTypes.map(value => ({
			value,
			label: PEOPLE_LEAVE_ENTITLEMENT_NAMES[value],
		}));
		const storedLeaveType = Number(editingEntry?.leaveType);

		if (editingEntry && storedLeaveType &&
			!allowedLeaveTypes.includes(storedLeaveType) &&
			PEOPLE_LEAVE_ENTITLEMENT_NAMES[storedLeaveType]) {
			options.push({
				value: storedLeaveType,
				label: PEOPLE_LEAVE_ENTITLEMENT_NAMES[storedLeaveType],
			});
		}

		return options;
	}, [allowedLeaveTypes, editingEntry]);

	const [createMyUnavailability, createStatus] = useMutation(CREATE_MY_UNAVAILABILITY);
	const [modifyMyUnavailability, modifyStatus] = useMutation(MODIFY_MY_UNAVAILABILITY);
	const [repeatMyUnavailability, repeatStatus] = useMutation(REPEAT_MY_UNAVAILABILITY);
	const isSaving = createStatus.loading || modifyStatus.loading || repeatStatus.loading;

	const onChangeStartDate = useCallback((value) => {
		setStartDate(value);
		setEndDate(current => (current || value));
		setRepeatUntilDate(current => (current || value));
		setRepeatWeekdays(current => (current.length ? current : (value ? [getRosterWeekday(value)] : [])));
	}, []);

	const toggleRepeatWeekday = useCallback((weekday) => {
		setRepeatWeekdays(current => (
			current.includes(weekday)
				? current.filter(value => value !== weekday)
				: [...current, weekday].sort()
		));
	}, []);

	const canRepeatUnavailability = !editingEntry && kind === PEOPLE_UNAVAILABILITY_KIND.UNAVAILABLE;
	const repeatPreviewDates = useMemo(() => (
		canRepeatUnavailability && repeatEnabled
			? buildRosterRecurringDates({
				sourceDate: startDate,
				repeatUntilDate,
				weekdays: repeatWeekdays,
				intervalWeeks: repeatIntervalWeeks,
			})
			: []
	), [canRepeatUnavailability, repeatEnabled, repeatIntervalWeeks, repeatUntilDate, repeatWeekdays, startDate]);

	const isShortNoticeUnavailability = isSelfServiceUnavailabilityShortNotice({
		kind,
		startDate,
		todayDate: today,
	});

	const submitEntry = useCallback(async () => {
		if (!startDate || !endDate) {
			return;
		}

		if (isShortNoticeUnavailability) {
			setFormError(SELF_SERVICE_UNAVAILABILITY_NOTICE_MESSAGE);
			return;
		}

		setFormError('');
		try {
			const input = {
				kind,
				leaveType: isLeaveForm ? leaveType : null,
				startDate,
				endDate,
				allDay,
				startMinutes: allDay ? null : startMinutes,
				endMinutes: allDay ? null : endMinutes,
				notes: notes || '',
			};
			if (!editingEntry?.id && repeatEnabled && canRepeatUnavailability) {
				const response = await repeatMyUnavailability({
					variables: {
						input: {
							kind,
							allDay,
							startMinutes: allDay ? null : startMinutes,
							endMinutes: allDay ? null : endMinutes,
							notes: notes || '',
							sourceDate: startDate,
							repeatUntilDate,
							weekdays: repeatWeekdays,
							intervalWeeks: repeatIntervalWeeks,
						},
					},
				});
				const result = response?.data?.repeatMyUnavailability || {};
				const createdCount = Number(result.createdCount || 0);
				const skippedCount = Number(result.skippedCount || 0);
				// Nothing was added, so stay on the form and say why.
				if (!createdCount && skippedCount) {
					setFormError(`${skippedCount} entries skipped because they already overlap existing entries.`);
					return;
				}
			}
			else if (editingEntry?.id) {
				await modifyMyUnavailability({
					variables: {
						input: {
							id: editingEntry.id,
							...input,
						},
					},
				});
			}
			else {
				await createMyUnavailability({ variables: { input } });
			}
			// Saved: leave before refreshing so a failed refresh cannot invite a duplicate submit.
			navigation.goBack();
			refetchEntries().catch(error => console.error('Roster refresh after save failed', error));
		}
		catch (error) {
			console.error('Unavailability save failed', error);
			setFormError(error?.message || 'Unable to save the entry. Please try again.');
		}
	}, [
		allDay,
		canRepeatUnavailability,
		createMyUnavailability,
		editingEntry?.id,
		endDate,
		endMinutes,
		isLeaveForm,
		isShortNoticeUnavailability,
		kind,
		leaveType,
		modifyMyUnavailability,
		navigation,
		notes,
		refetchEntries,
		repeatEnabled,
		repeatIntervalWeeks,
		repeatMyUnavailability,
		repeatUntilDate,
		repeatWeekdays,
		startDate,
		startMinutes,
	]);

	const entryKindAllowed = isLeaveForm
		? canRequestLeave && allowedLeaveTypes.includes(Number(leaveType))
		: canRequestUnavailability;
	const minimumEntryDate = editingEntry ? null : today;
	const canSubmitEntry = Boolean(employmentTypeReady && entryKindAllowed &&
		startDate && endDate && endDate >= startDate &&
		(allDay || endMinutes > startMinutes) &&
		(!repeatEnabled || !canRepeatUnavailability || (repeatUntilDate && repeatPreviewDates.length)) &&
		!isShortNoticeUnavailability);

	return {
		isLeaveForm,
		selfServiceCutoffDate,
		leaveType,
		setLeaveType,
		leaveTypeOptions,
		leaveBalances,
		startDate,
		onChangeStartDate,
		endDate,
		setEndDate,
		allDay,
		setAllDay,
		startMinutes,
		setStartMinutes,
		endMinutes,
		setEndMinutes,
		notes,
		setNotes,
		canRepeatUnavailability,
		repeatEnabled,
		setRepeatEnabled,
		repeatUntilDate,
		setRepeatUntilDate,
		repeatIntervalWeeks,
		setRepeatIntervalWeeks,
		repeatWeekdays,
		toggleRepeatWeekday,
		repeatPreviewDates,
		isShortNoticeUnavailability,
		minimumEntryDate,
		canSubmitEntry,
		isSaving,
		formError,
		submitEntry,
	};
};

export default useLeaveRequestForm;
